// keyrd_console/src/components/PilotShadowState.js
import React, { useEffect, useMemo, useState } from "react";
import { getPilotShadowState } from "../api/pilotAdmin";
import PilotGovernanceBanner from "./PilotGovernanceBanner";
import PilotStatusPill from "./PilotStatusPill";
import PilotDataSourceBadge from "./PilotDataSourceBadge";

const pageStyle = {
  padding: 20,
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const titleRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 12,
};

const titleStyle = {
  fontSize: 24,
  fontWeight: 700,
  color: "#0f172a",
  margin: 0,
};

const subtitleStyle = {
  color: "#475569",
  fontSize: 14,
  marginTop: 4,
};

const warningStyle = {
  padding: 12,
  borderRadius: 10,
  background: "#fff7ed",
  color: "#9a3412",
  border: "1px solid #fdba74",
  fontSize: 13,
  fontWeight: 600,
};

const controlRowStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
};

const inputStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  minWidth: 260,
};

const selectStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
};

const cardGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: 12,
};

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  cursor: "pointer",
};

const cardTitleStyle = {
  fontSize: 16,
  fontWeight: 700,
  color: "#0f172a",
};

const metaTextStyle = {
  fontSize: 13,
  color: "#64748b",
};

const rowWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
};

const loadingStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#334155",
};

const errorStyle = {
  padding: 14,
  borderRadius: 10,
  background: "#fef2f2",
  color: "#991b1b",
  border: "1px solid #fecaca",
};

const emptyStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#475569",
};

function normalizeRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.participants)) return payload.participants;
  if (Array.isArray(payload?.shadow_rows)) return payload.shadow_rows;
  return [];
}

function matchesQuery(row, query) {
  if (!query) return true;
  const haystack = [
    row.participant_uuid,
    row.participant_label,
    row.evidence_state,
    row.evidence_family,
    row.suppression_reason,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(query.toLowerCase());
}

function filterRows(rows, query, filter) {
  return rows.filter((row) => {
    const queryOk = matchesQuery(row, query);
    const filterOk =
      filter === "all" ||
      String(row.suppression_status || "").toLowerCase() === filter ||
      String(row.cooldown_status || "").toLowerCase() === filter ||
      String(row.quiet_hours_status || "").toLowerCase() === filter;
    return queryOk && filterOk;
  });
}

function PilotShadowState({ auth, onSelectParticipant }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await getPilotShadowState(auth);
        if (!cancelled) {
          setPayload(res?.data || res || {});
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load pilot shadow state.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const data = payload || {};
  const rows = normalizeRows(data);
  const filteredRows = useMemo(
    () => filterRows(rows, query, filter),
    [rows, query, filter]
  );

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={loadingStyle}>Loading pilot shadow state…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={errorStyle}>{error}</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={titleRowStyle}>
        <div>
          <h1 style={titleStyle}>Shadow State</h1>
          <div style={subtitleStyle}>
            Simulation-only decision visibility across participants. This page must not imply live intervention.
          </div>
        </div>
        <PilotDataSourceBadge source={data.data_source} stub={Boolean(data.stub)} />
      </div>

      <PilotGovernanceBanner auth={auth} system={data.system || data} />

      <div style={warningStyle}>
        Simulation-only surface. This page is for shadow decision inspection, not live action.
      </div>

      <div style={controlRowStyle}>
        <input
          style={inputStyle}
          type="text"
          value={query}
          placeholder="Search participant, evidence, suppression…"
          onChange={(e) => setQuery(e.target.value)}
        />
        <select style={selectStyle} value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">All states</option>
          <option value="suppressed">Suppressed</option>
          <option value="cooldown_active">Cooldown Active</option>
          <option value="quiet_hours_active">Quiet Hours Active</option>
        </select>
      </div>

      {!filteredRows.length ? (
        <div style={emptyStyle}>No shadow-state rows matched the current filters.</div>
      ) : (
        <div style={cardGridStyle}>
          {filteredRows.map((row, idx) => (
            <div
              key={row.participant_uuid || idx}
              style={cardStyle}
              onClick={() =>
                typeof onSelectParticipant === "function" &&
                row.participant_uuid &&
                onSelectParticipant(row.participant_uuid)
              }
            >
              <div style={cardTitleStyle}>
                {row.participant_label || "Unnamed Participant"}
              </div>
              <div style={metaTextStyle}>{row.participant_uuid || "No UUID"}</div>

              <div style={rowWrapStyle}>
                <PilotStatusPill
                  status={row.suppression_status || "unknown"}
                  label={`Suppression: ${row.suppression_status || "unknown"}`}
                />
                <PilotStatusPill
                  status={row.cooldown_status || "unknown"}
                  label={`Cooldown: ${row.cooldown_status || "unknown"}`}
                />
                <PilotStatusPill
                  status={row.quiet_hours_status || "unknown"}
                  label={`Quiet Hours: ${row.quiet_hours_status || "unknown"}`}
                />
              </div>

              <div style={metaTextStyle}>
                Evidence State: {row.evidence_state || "unknown"}
              </div>
              <div style={metaTextStyle}>
                Evidence Family: {row.evidence_family || "unknown"}
              </div>
              <div style={metaTextStyle}>
                Evidence Confidence: {row.evidence_confidence || "unknown"}
              </div>
              <div style={metaTextStyle}>
                Suppression Reason: {row.suppression_reason || "none"}
              </div>
              <div style={metaTextStyle}>
                Last Shadow Decision: {row.last_shadow_decision_at_utc || "n/a"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PilotShadowState;